import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang, categoryLabels } from "@/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["rings", "necklaces", "earrings", "bracelets", "giftcards"];
const LANGS = ["it", "de", "fr", "en"];
const emptyForm = {
  name: { it: "", de: "", fr: "", en: "" },
  description: { it: "", de: "", fr: "", en: "" },
  category: "rings", price: "", images: "", stock: 12, featured: false,
};

function ProductForm({ initial, onSaved, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const setLang = (field, lng, val) => setForm((f) => ({ ...f, [field]: { ...f[field], [lng]: val } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, category: form.category,
      price: parseFloat(form.price) || 0, images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock) || 0, featured: !!form.featured,
    };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success(t("save"));
      onSaved(); onClose();
    } catch (e) {
      toast.error("Error saving product");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880]";

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("name")}</label>
        <div className="grid grid-cols-2 gap-2">
          {LANGS.map((l) => (
            <input key={l} placeholder={l.toUpperCase()} value={form.name[l]} onChange={(e) => setLang("name", l, e.target.value)} data-testid={`admin-name-${l}`} className={inputCls} />
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("description")}</label>
        <div className="grid grid-cols-2 gap-2">
          {LANGS.map((l) => (
            <input key={l} placeholder={l.toUpperCase()} value={form.description[l]} onChange={(e) => setLang("description", l, e.target.value)} data-testid={`admin-desc-${l}`} className={inputCls} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("category")}</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-category" className={inputCls}>
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("price")} (CHF)</label>
          <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-price" className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("stock")}</label>
          <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-[#1A1F2C]">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-featured" />
            {t("featured")}
          </label>
        </div>
      </div>
      <div>
        <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("images_url")} (1 / riga)</label>
        <textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} data-testid="admin-image" rows={3} className={inputCls} placeholder="https://...&#10;https://..." />
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-product"
        className="w-full bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-3 hover:bg-[#C5A880] transition-colors disabled:opacity-60">
        {saving ? t("processing") : t("save")}
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const { t, tl } = useLang();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(emptyForm);
  const [discounts, setDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ code: "", percent: 10 });
  const [shipping, setShipping] = useState([]);
  const [settings, setSettings] = useState({ announcement: { it: "", de: "", fr: "", en: "" }, announcement_active: false, free_shipping_threshold: 200 });

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  const loadProducts = () => api.get("/products").then((res) => setProducts(res.data));
  const loadOrders = () => api.get("/admin/orders").then((res) => setOrders(res.data)).catch(() => {});
  const loadDiscounts = () => api.get("/discounts").then((res) => setDiscounts(res.data)).catch(() => {});
  const loadShipping = () => api.get("/admin/shipping").then((res) => setShipping(res.data)).catch(() => {});
  const loadSettings = () => api.get("/settings").then((res) => setSettings({ announcement: res.data.announcement || { it: "", de: "", fr: "", en: "" }, announcement_active: res.data.announcement_active, free_shipping_threshold: res.data.free_shipping_threshold })).catch(() => {});

  useEffect(() => { if (user?.role === "admin") { loadProducts(); loadOrders(); loadDiscounts(); loadShipping(); loadSettings(); } }, [user]);

  const saveSettings = async () => {
    try {
      await api.put("/settings", { announcement: settings.announcement, announcement_active: settings.announcement_active, free_shipping_threshold: parseFloat(settings.free_shipping_threshold) || 0 });
      toast.success(t("save"));
    } catch (e) { toast.error("Error"); }
  };

  const addDiscount = async () => {
    if (!newDiscount.code.trim()) return;
    try {
      await api.post("/discounts", { code: newDiscount.code, percent: parseInt(newDiscount.percent) || 0, active: true });
      toast.success(t("save")); setNewDiscount({ code: "", percent: 10 }); loadDiscounts();
    } catch (e) { toast.error("Error"); }
  };
  const delDiscount = async (id) => { await api.delete(`/discounts/${id}`); loadDiscounts(); };
  const saveShipping = async (m) => {
    await api.put(`/shipping/${m.id}`, { name: m.name, price: parseFloat(m.price) || 0, free_over: parseFloat(m.free_over) || 0, active: m.active });
    toast.success(t("save")); loadShipping();
  };
  const updateShipField = (id, field, val) => setShipping((prev) => prev.map((m) => m.id === id ? { ...m, [field]: val } : m));

  if (!user || user.role !== "admin") return null;

  const openNew = () => { setEditing(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing({
      id: p.id, name: p.name, description: p.description, category: p.category,
      price: p.price, images: (p.images || []).join("\n"), stock: p.stock, featured: p.featured,
    });
    setDialogOpen(true);
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); loadProducts();
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 fade-up min-h-[70vh]">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-serif-display text-4xl text-[#1A1F2C]">{t("admin_panel")}</h1>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }}
          className="text-xs tracking-luxe uppercase border-b border-[#1A1F2C] pb-1 hover:text-[#C5A880] hover:border-[#C5A880] transition-colors">
          {t("logout")}
        </button>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="rounded-none bg-white border border-[#EAE6E1] mb-8">
          <TabsTrigger value="products" data-testid="admin-tab-products" className="text-xs tracking-luxe uppercase rounded-none">{t("admin_products")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders" className="text-xs tracking-luxe uppercase rounded-none">{t("admin_orders")}</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="admin-tab-discounts" className="text-xs tracking-luxe uppercase rounded-none">{t("admin_discounts")}</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="admin-tab-shipping" className="text-xs tracking-luxe uppercase rounded-none">{t("admin_shipping")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="text-xs tracking-luxe uppercase rounded-none">{t("admin_settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="flex justify-end mb-5">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button data-testid="admin-add-product" onClick={openNew}
                  className="flex items-center gap-2 bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase px-5 py-3 hover:bg-[#C5A880] transition-colors">
                  <Plus className="w-4 h-4" /> {t("add_product")}
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#EAE6E1] bg-[#FAF9F6] max-w-2xl">
                <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing.id ? t("edit") : t("add_product")}</DialogTitle></DialogHeader>
                <ProductForm initial={editing} onSaved={loadProducts} onClose={() => setDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#EAE6E1] overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead className="border-b border-[#EAE6E1] text-left text-xs tracking-luxe uppercase text-[#6B7280]">
                <tr>
                  <th className="p-4">{t("name")}</th>
                  <th className="p-4">{t("category")}</th>
                  <th className="p-4">{t("price")}</th>
                  <th className="p-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-[#EAE6E1]/60" data-testid={`admin-product-row-${p.id}`}>
                    <td className="p-4 flex items-center gap-3">
                      <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover bg-[#F0EAE1]" />
                      <span className="font-serif-display text-base">{tl(p.name)}</span>
                    </td>
                    <td className="p-4 text-[#6B7280]">{p.category}</td>
                    <td className="p-4">{formatPrice(p.price)}</td>
                    <td className="p-4 text-right">
                      <div className="flex gap-3 justify-end">
                        <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#1A1F2C] hover:text-[#C5A880]"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p.id)} className="text-[#6B7280] hover:text-[#991B1B]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white border border-[#EAE6E1] overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-orders-table">
              <thead className="border-b border-[#EAE6E1] text-left text-xs tracking-luxe uppercase text-[#6B7280]">
                <tr>
                  <th className="p-4">{t("email")}</th>
                  <th className="p-4">{t("total")}</th>
                  <th className="p-4">{t("status")}</th>
                  <th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-[#EAE6E1]/60">
                    <td className="p-4">{o.user_email || "—"}</td>
                    <td className="p-4">{new Intl.NumberFormat(o.currency === "eur" ? "de-DE" : "de-CH", { style: "currency", currency: (o.currency || "chf").toUpperCase() }).format(o.amount || 0)}</td>
                    <td className="p-4">{o.payment_status}</td>
                    <td className="p-4 text-[#6B7280]">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="discounts">
          <div className="bg-white border border-[#EAE6E1] p-6 mb-6 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("code")}</label>
              <input value={newDiscount.code} onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })} data-testid="discount-code-input"
                className="bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#C5A880]" placeholder="ESTATE20" />
            </div>
            <div>
              <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("percent")} %</label>
              <input type="number" value={newDiscount.percent} onChange={(e) => setNewDiscount({ ...newDiscount, percent: e.target.value })} data-testid="discount-percent-input"
                className="w-24 bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880]" />
            </div>
            <button onClick={addDiscount} data-testid="discount-add"
              className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase px-5 py-2.5 hover:bg-[#C5A880] transition-colors">{t("add_product")}</button>
          </div>
          <div className="bg-white border border-[#EAE6E1] overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-discounts-table">
              <thead className="border-b border-[#EAE6E1] text-left text-xs tracking-luxe uppercase text-[#6B7280]">
                <tr><th className="p-4">{t("code")}</th><th className="p-4">{t("percent")}</th><th className="p-4 text-right"></th></tr>
              </thead>
              <tbody>
                {discounts.map((d) => (
                  <tr key={d.id} className="border-b border-[#EAE6E1]/60" data-testid={`discount-row-${d.code}`}>
                    <td className="p-4 font-medium tracking-wide">{d.code}</td>
                    <td className="p-4">-{d.percent}%</td>
                    <td className="p-4 text-right">
                      <button data-testid={`discount-delete-${d.id}`} onClick={() => delDiscount(d.id)} className="text-[#6B7280] hover:text-[#991B1B]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="shipping">
          <div className="bg-white border border-[#EAE6E1] overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-shipping-table">
              <thead className="border-b border-[#EAE6E1] text-left text-xs tracking-luxe uppercase text-[#6B7280]">
                <tr><th className="p-4">{t("name")}</th><th className="p-4">{t("price")} (CHF)</th><th className="p-4">{t("free_over")} (CHF)</th><th className="p-4 text-right"></th></tr>
              </thead>
              <tbody>
                {shipping.map((m) => (
                  <tr key={m.id} className="border-b border-[#EAE6E1]/60" data-testid={`shipping-row-${m.id}`}>
                    <td className="p-4">{tl(m.name)}</td>
                    <td className="p-4">
                      <input type="number" value={m.price} onChange={(e) => updateShipField(m.id, "price", e.target.value)} data-testid={`shipping-price-${m.id}`}
                        className="w-24 bg-[#FAF9F6] border border-[#EAE6E1] px-2 py-1.5 text-sm focus:outline-none focus:border-[#C5A880]" />
                    </td>
                    <td className="p-4">
                      <input type="number" value={m.free_over} onChange={(e) => updateShipField(m.id, "free_over", e.target.value)} data-testid={`shipping-freeover-${m.id}`}
                        className="w-24 bg-[#FAF9F6] border border-[#EAE6E1] px-2 py-1.5 text-sm focus:outline-none focus:border-[#C5A880]" />
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => saveShipping(m)} data-testid={`shipping-save-${m.id}`}
                        className="text-xs tracking-luxe uppercase border border-[#1A1F2C] px-4 py-1.5 hover:bg-[#1A1F2C] hover:text-white transition-colors">{t("save")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="bg-white border border-[#EAE6E1] p-8 max-w-2xl space-y-6" data-testid="admin-settings-form">
            <div>
              <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("announcement_text")}</label>
              <div className="grid grid-cols-2 gap-2">
                {LANGS.map((l) => (
                  <input key={l} placeholder={l.toUpperCase()} value={settings.announcement[l]}
                    onChange={(e) => setSettings({ ...settings, announcement: { ...settings.announcement, [l]: e.target.value } })}
                    data-testid={`settings-announcement-${l}`}
                    className="bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880]" />
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#1A1F2C]">
              <input type="checkbox" checked={settings.announcement_active} data-testid="settings-announcement-active"
                onChange={(e) => setSettings({ ...settings, announcement_active: e.target.checked })} />
              {t("announcement_enable")}
            </label>
            <div>
              <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{t("free_shipping_threshold_label")}</label>
              <input type="number" value={settings.free_shipping_threshold} data-testid="settings-threshold"
                onChange={(e) => setSettings({ ...settings, free_shipping_threshold: e.target.value })}
                className="w-40 bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880]" />
            </div>
            <button onClick={saveSettings} data-testid="settings-save"
              className="bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase px-8 py-3 hover:bg-[#C5A880] transition-colors">{t("save")}</button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
